import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { marketsApi } from '../../api/marketsApi';
import { useLocationOptions } from '../Admin/shared/useLocationOptions';
import { useCollectionSync } from '../../hooks/useCollectionSync';
import NlimsDataTable from '../shared/NlimsDataTable/NlimsDataTable';
import { FilterBar, SearchRow } from './shared';
import { useAuth } from '../../context/AuthContext';
import {
  labelForValue,
  MARKET_OPERATIONAL_STATUS_STYLE,
  MARKET_SCHEDULE_OPTIONS,
  MARKET_STATUS_OPTIONS,
  MARKET_TYPE_OPTIONS
} from './marketTradOptions';
import { ActionDropdown, anchorRect, DeleteConfirmModal, RejectNotesModal, actionCellHtml as sharedActionCellHtml, WORKFLOW_RULES } from '../shared/RowActionMenu';
import { useNotifications } from '../../context/NotificationContext';
import { registryCentralApi } from '../../api/registryCentralApi';

function normalizeForCompare(value) {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

// Client-side filtering, mirroring the dropdown filter set — the backend
// doesn't reliably honor these query params, so filters are applied here
// against the already-loaded records instead (same approach Conflicts &
// Dispute Records uses).
function filterMarketRows(rows, filters) {
  const state = normalizeForCompare(filters.state);
  const lga = normalizeForCompare(filters.lga);
  const type = normalizeForCompare(filters.type);
  const status = normalizeForCompare(filters.status);

  return rows.filter((row) => {
    if (state && normalizeForCompare(row.state) !== state) return false;
    if (lga && normalizeForCompare(row.lga) !== lga) return false;
    if (type && normalizeForCompare(row.market_type) !== type) return false;
    if (status && normalizeForCompare(row.status) !== status) return false;
    return true;
  });
}

function parseError(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function normalizeMarketRow(row = {}) {
  const status = row.status ?? '';
  const marketType = row.market_type ?? row.marketType ?? '';
  const marketSchedule = row.market_schedule ?? row.marketSchedule ?? '';
  return {
    ...row,
    id: row.uuid ?? row.id ?? row.market_uuid ?? `${row.market_name ?? 'market'}-${row.state ?? ''}`,
    uuid: row.uuid ?? row.id ?? row.market_uuid ?? null,
    market_name: row.market_name ?? row.marketName ?? '',
    marketName: row.market_name ?? row.marketName ?? '',
    market_type: marketType,
    market_type_label: labelForValue(MARKET_TYPE_OPTIONS, marketType),
    market_schedule: marketSchedule,
    market_schedule_label: labelForValue(MARKET_SCHEDULE_OPTIONS, marketSchedule),
    status,
    status_label: labelForValue(MARKET_STATUS_OPTIONS, status),
    state: row.state ?? '',
    lga: row.lga ?? '',
    contact: row.contact ?? '',
    location: row.location ?? '',
    established: row.established ?? '',
    description: row.description ?? '',
  };
}

// ── Status badge (DataTables render — returns HTML string) ───────────────────
function marketStatusBadgeHtml(rawStatus, label) {
  const key = String(rawStatus ?? '').trim().toLowerCase().replace(/\s+/g, '_');
  const s = MARKET_OPERATIONAL_STATUS_STYLE[key];
  const text = label || rawStatus || '—';
  if (!s) {
    return `<span style="font-size:11px;font-weight:600;color:#6b7280;">${text}</span>`;
  }
  return (
    `<span style="display:inline-flex;align-items:center;padding:2px 10px;border-radius:999px;` +
    `font-size:11px;font-weight:600;letter-spacing:.02em;white-space:nowrap;` +
    `background:${s.bg};color:${s.color};border:1px solid ${s.border};">${text}</span>`
  );
}

// Record workflow status pill (draft → submitted → validated → approved /
// rejected) — same palette as Market Prices.
const DATA_STATUS_BADGE_STYLE = {
  draft:     'background:#f5f5f4;color:#888780;border:1px solid #d6d3d1;',
  submitted: 'background:#eff6ff;color:#185FA5;border:1px solid #bfdbfe;',
  validated: 'background:#fffbeb;color:#BA7517;border:1px solid #fde68a;',
  approved:  'background:#f0fdf4;color:#3B6D11;border:1px solid #bbf7d0;',
  rejected:  'background:#fff1f2;color:#A32D2D;border:1px solid #fecdd3;',
};

function dataStatusBadgeHtml(status) {
  const key = String(status || 'draft').toLowerCase();
  const label = key.charAt(0).toUpperCase() + key.slice(1);
  return `<span style="display:inline-flex;padding:2px 10px;border-radius:9999px;font-size:11px;font-weight:600;white-space:nowrap;${DATA_STATUS_BADGE_STYLE[key] ?? DATA_STATUS_BADGE_STYLE.draft}">${label}</span>`;
}

// ── Table columns ─────────────────────────────────────────────────────────────
const TABLE_COLUMNS = [
  { title: 'Market Name',    data: 'market_name',         sortable: true },
  { title: 'State',          data: 'state',               sortable: true },
  { title: 'LGA',            data: 'lga',                 sortable: true },
  { title: 'GPS Coordinates', data: 'gps_coordinates',   sortable: true },
  { title: 'Market Type',    data: 'market_type_label',   sortable: true },
  { title: 'Schedule',       data: 'market_schedule_label', sortable: true },
  {
    title: 'Status',
    data: 'status_label',
    sortable: true,
    render: (data, type, row) =>
      type === 'display'
        ? marketStatusBadgeHtml(row?.status, data)
        : (data ?? ''),
  },
  {
    title: 'Data Status',
    data: 'data_status',
    sortable: true,
    render: (data, type) => (type === 'display' ? dataStatusBadgeHtml(data) : (data ?? 'draft')),
  },
  {
    title: 'Action',
    data: null,
    sortable: false,
    render: (_d, type) => (type === 'display' ? sharedActionCellHtml() : ''),
  },
];

// ── Main component ────────────────────────────────────────────────────────────
export default function MarketRegistry({ onCreateNew, refreshKey = 0 }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleId = user?.roleId ?? 'R06';
  const [draftFilters, setDraftFilters] = useState({ state: '', lga: '', type: '', status: '' });
  const [appliedFilters, setAppliedFilters] = useState({ state: '', lga: '', type: '', status: '' });
  const [search, setSearch] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMenu, setActionMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [quickBusy, setQuickBusy] = useState('');
  const { pushWorkflowNotification } = useNotifications();
  // Registry Central markets that were validated but still need their full
  // Market Registry details — completing one makes the entry Active.
  const [toComplete, setToComplete] = useState([]);
  const loadToComplete = useCallback(async () => {
    const rows = await registryCentralApi.listPendingCompletion('market');
    const homeState = roleId === 'R01' ? '' : String(user?.state ?? '').toLowerCase();
    setToComplete(rows.filter((r) => !homeState || String(r.state).toLowerCase() === homeState));
  }, [roleId, user?.state]);
  useEffect(() => { loadToComplete(); }, [loadToComplete]);
  useCollectionSync(registryCentralApi.collectionKey, loadToComplete);

  const { stateOptions, lgaOptions, loadingStates, loadingLgas } = useLocationOptions(draftFilters.state);

  const filterDefs = useMemo(() => ([
    {
      key: 'state',
      label: 'State',
      options: stateOptions,
      defaultLabel: loadingStates ? 'Loading States...' : 'All States',
    },
    {
      key: 'lga',
      label: 'LGA',
      options: lgaOptions,
      defaultLabel: draftFilters.state ? (loadingLgas ? 'Loading LGAs...' : 'All LGAs') : 'Select State first',
      disabled: !draftFilters.state || loadingLgas,
    },
    {
      key: 'type',
      label: 'Market Type',
      options: MARKET_TYPE_OPTIONS,
      defaultLabel: 'All Types',
    },
    {
      key: 'status',
      label: 'Status',
      options: MARKET_STATUS_OPTIONS,
      defaultLabel: 'All Statuses',
    },
  ]), [draftFilters.state, lgaOptions, loadingLgas, loadingStates, stateOptions]);

  // Applies immediately on every change — live filtering, matching the rest
  // of the app, instead of requiring a separate "Apply" click.
  const handleFilterChange = useCallback((key, value) => {
    setDraftFilters((prev) => {
      const next = key === 'state' ? { ...prev, state: value, lga: '' } : { ...prev, [key]: value };
      setAppliedFilters(next);
      return next;
    });
  }, []);

  const handleApply = useCallback(() => setAppliedFilters(draftFilters), [draftFilters]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    marketsApi.listAll({
      per_page: 1000,
      q: search.trim() || undefined,
    })
      .then((payload) => { if (active) setRecords((payload.items || []).map(normalizeMarketRow)); })
      .catch((err) => { if (active) { setError(parseError(err, 'Failed to load market records.')); setRecords([]); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [refreshKey, search]);

  const reloadRecords = useCallback(() => {
    setLoading(true);
    marketsApi.listAll({
      per_page: 1000,
      q: search.trim() || undefined,
    })
      .then((payload) => { setRecords((payload.items || []).map(normalizeMarketRow)); })
      .catch((err) => { setError(parseError(err, 'Failed to load market records.')); setRecords([]); })
      .finally(() => setLoading(false));
  }, [search]);

  useCollectionSync(marketsApi.collectionKey, reloadRecords);

  const filteredRecords = useMemo(() => filterMarketRows(records, appliedFilters), [records, appliedFilters]);

  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === 'menu' || action === 'view') setActionMenu({ row, rect });
  }, []);

  // Submit / Validate / Approve / Reject — each through its own backend
  // endpoint; which of these the menu offers comes from WORKFLOW_RULES.markets.
  const handleQuickStatus = useCallback(async (row, status, notes = '') => {
    setError('');
    setQuickBusy(status);
    try {
      if (status === 'submitted') await marketsApi.submit(row.uuid);
      else if (status === 'validated') await marketsApi.validate(row.uuid);
      else if (status === 'approved') await marketsApi.approve(row.uuid);
      else if (status === 'rejected') await marketsApi.reject(row.uuid, notes);
      setRecords((prev) => prev.map((r) => (
        r.uuid === row.uuid ? { ...r, data_status: status, ...(status === 'rejected' ? { rejection_notes: notes } : {}) } : r
      )));
      pushWorkflowNotification({
        moduleKey: 'markets', roleCan: WORKFLOW_RULES.markets.roleCan, newStatus: status,
        actorRoleId: roleId, recordLabel: row.market_name, notes,
      });
    } catch (e) {
      setError(parseError(e, `Failed to update the market to ${status}.`));
    } finally {
      setQuickBusy('');
    }
  }, [roleId, pushWorkflowNotification]);

  const handleRejectConfirm = useCallback(async (notes) => {
    if (!rejectTarget) return;
    await handleQuickStatus(rejectTarget, 'rejected', notes);
    setRejectTarget(null);
  }, [rejectTarget, handleQuickStatus]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await marketsApi.remove(deleteTarget.uuid);
      setRecords((prev) => prev.filter((r) => r.uuid !== deleteTarget.uuid));
      setDeleteTarget(null);
    } catch (e) {
      setError(parseError(e, 'Failed to delete market record.'));
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteTarget]);

  return (
    <div className="space-y-4">
      <FilterBar
        filters={filterDefs}
        filterValues={draftFilters}
        onFilterChange={handleFilterChange}
        onApply={handleApply}
      />

      <SearchRow
        searchValue={search}
        onSearch={setSearch}
        onCreate={onCreateNew ?? (() => {})}
        canCreate={Boolean(onCreateNew)}
        placeholder="Search by market name, state, or LGA..."
      />

      {onCreateNew && toComplete.length > 0 && (
        <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
          <p className="mb-2 text-xs font-semibold text-blue-800">
            {toComplete.length} validated market{toComplete.length === 1 ? '' : 's'} from Registry Central awaiting completion
          </p>
          <div className="flex flex-wrap gap-2">
            {toComplete.map((entry) => (
              <button key={entry.uuid} type="button"
                onClick={() => navigate('/markets-trade/new?subModule=registry', { state: { registryEntry: entry } })}
                className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100">
                {entry.name} <span className="text-blue-400">· {[entry.lga, entry.state].filter(Boolean).join(', ')}</span> — Complete
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {loading && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">
          Loading market records...
        </div>
      )}

      <NlimsDataTable
        tableId="market-registry-table"
        columns={TABLE_COLUMNS}
        data={filteredRecords}
        serverSide={false}
        hideSearch
        pageLength={25}
        onRowAction={handleRowAction}
        defaultSort={[0, 'asc']}
      />

      {quickBusy && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">Updating record status...</div>
      )}

      {actionMenu && !deleteBusy && !quickBusy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.markets}
          deleteLabel="Delete Market"
          onClose={() => setActionMenu(null)}
          onView={() => { navigate(`/markets-trade/view/${actionMenu.row.uuid}?subModule=registry`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onEdit={() => { navigate(`/markets-trade/edit/${actionMenu.row.uuid}?subModule=registry`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onSubmit={() => handleQuickStatus(actionMenu.row, 'submitted')}
          onValidate={() => handleQuickStatus(actionMenu.row, 'validated')}
          onApprove={() => handleQuickStatus(actionMenu.row, 'approved')}
          onReject={() => { setRejectTarget(actionMenu.row); setActionMenu(null); }}
          onDelete={() => { setDeleteTarget(actionMenu.row); setActionMenu(null); }}
        />
      )}

      {rejectTarget && (
        <RejectNotesModal
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          row={deleteTarget}
          entityLabel={deleteTarget.market_name || 'this market'}
          noun="market record"
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={deleteBusy}
        />
      )}
    </div>
  );
}
